import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone, Share } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";

const DISMISS_KEY = "ce_install_dismissed_at";
const DISMISS_DAYS = 7;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Dismiss check
    const last = localStorage.getItem(DISMISS_KEY);
    if (last && (Date.now() - Number(last)) < DISMISS_DAYS * 86400000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS fallback (Safari doesn't fire beforeinstallprompt)
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIOS && !isStandalone) {
      setTimeout(() => { setIosHint(true); setShow(true); }, 1500);
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setShow(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-96 z-50 bg-white border border-slate-200 shadow-xl rounded-md overflow-hidden" data-testid="install-prompt">
      <div className="flex items-start gap-3 p-4">
        <img src={LOGO_URL} alt="logo" className="h-12 w-12 object-contain rounded-md shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-sm text-slate-900">Install Cleavie's app</div>
          <p className="text-xs text-slate-600 mt-0.5">
            {iosHint
              ? "Tap the Share icon, then 'Add to Home Screen'."
              : "Book water, bins, rentals & trucking right from your home screen."}
          </p>
          <div className="mt-3 flex items-center gap-2">
            {iosHint ? (
              <div className="inline-flex items-center gap-1 text-xs font-bold text-[#1D4ED8]">
                <Share className="h-3.5 w-3.5" /> Share → Add to Home Screen
              </div>
            ) : (
              <Button size="sm" onClick={install} className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold h-8" data-testid="install-btn">
                <Download className="h-3.5 w-3.5 mr-1" /> Install
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss} className="text-slate-500 h-8" data-testid="install-dismiss-btn">
              Later
            </Button>
          </div>
        </div>
        <button onClick={dismiss} className="p-1 text-slate-400 hover:text-slate-700 shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
